import React, { useCallback, useEffect } from 'react'
import { FulfilmentType } from '../../../../models/fulfilment'
import { useAppDispatch, useAppSelector } from '../../../../state/hooks'
import {
  selectCalculateTaxError,
  selectIsEcomOrder,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import { useSaleHasServiceOrder } from '../../../sale/useSaleHasServiceOrder'
import { LineItemTab } from '../../../../../types/lineItemTabs'
import { useHasOutdatedTaxCategory } from '../fulfilment/hook/useHasOutdatedTaxCategory'
import { useLineItemHasFulfillmentAddressError } from '../fulfilment/hook/useLineItemHasFulfillmentAddressError'
import {
  selectLineItemCurrentTab,
  setLineItemCurrentTab,
} from '../../../../state/slices/uiStateSlice'
import { selectSaleReturnFor } from '../../../../state/selectors'
import { getActiveLineItemTab } from './getActiveLineItemTab'

export interface LineItemTabsProps {
  /**
   * @deprecated only used in legacy implementation of LineItemTabs, will be removed in future versions
   * Current implementation uses currentTab from redux store
   */
  defaultTab?: LineItemTab

  /**
   * Default onChange handler is to update the current tab in the redux store. onChange handler can be provided to execute additional logic when the tab changes.
   * @param selected
   * @returns
   */
  onChange?: (selected: LineItemTab) => void

  /**
   * @deprecated only used in legacy implementation of LineItemTabs, will be removed in future versions
   */
  children?: React.ReactNode
  fulfillmentType?: FulfilmentType
  lineItemId?: string
}

export const useLineItemTabsModel = (props: LineItemTabsProps) => {
  const dispatch = useAppDispatch()
  const { onChange } = props
  const requestedTab = useAppSelector(selectLineItemCurrentTab)
  const isEcomOrder = useAppSelector(selectIsEcomOrder)
  const saleHasServiceOrder = useSaleHasServiceOrder()
  const calculateTaxError = useAppSelector(selectCalculateTaxError)
  const isReferencedReturn = !!useAppSelector(selectSaleReturnFor)
  const hasAddressErrorForLineItemFulfillmentDelivery =
    (useLineItemHasFulfillmentAddressError() || !!calculateTaxError) &&
    props.fulfillmentType === FulfilmentType.DELIVERY
  const hasOutdatedTaxCategory =
    useHasOutdatedTaxCategory(props.lineItemId) &&
    props.fulfillmentType === FulfilmentType.DELIVERY
  const hasFulfillmentError =
    hasAddressErrorForLineItemFulfillmentDelivery || hasOutdatedTaxCategory

  const shouldShowTabs = !saleHasServiceOrder
  const currentTab = getActiveLineItemTab({
    requestedTab,
    isReferencedReturn,
    shouldShowTabs,
  })

  const params = {
    [LineItemTab.DETAIL]: {
      value: LineItemTab.DETAIL,
    },
    [LineItemTab.FULFILMENT]: {
      value: LineItemTab.FULFILMENT,
      disabled: isReferencedReturn,
      hasWarning:
        currentTab !== LineItemTab.FULFILMENT &&
        hasFulfillmentError &&
        !isEcomOrder,
    },
  }

  useEffect(() => {
    if (currentTab !== requestedTab) {
      dispatch(setLineItemCurrentTab(currentTab))
      onChange?.(currentTab)
    }
  }, [currentTab, requestedTab, onChange, dispatch])

  const onTabChange = useCallback(
    (tab: string) => {
      dispatch(setLineItemCurrentTab(tab as LineItemTab))
      onChange?.(tab as LineItemTab)
    },
    [dispatch, onChange]
  )

  return { currentTab, params, onTabChange, shouldShowTabs }
}
